import type { Injury } from '@lmc/validations'
import useBodyMaps from 'hooks/useBodyMaps'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { IncidentCaseV2ReadPanelProps } from '../../incidentCaseV2Steps'
import { step5Answers } from '../../steps/injuriesAndDamages/appInjuries'
import { DAMAGE_TYPE_KEYS } from '../../steps/injuriesAndDamages/DamageEntries'
import { useServiceUserWords } from '../../useServiceUserWords'
import { ReadEntry, ReadItem, ReadWide } from '../readLayout'
import {
  detailsUnderNo,
  embeddedNames,
  type InjuriesCase,
  injuredPerson,
  injuryBodyMapIds,
  injuryEntries,
  injuryTitle,
  linkedBodyMap,
  recordedDamages,
  yesNo
} from '../readRules'
import {
  type PersonNames,
  useServiceUserName,
  useTeamMemberName
} from '../usePersonName'

const TEST_ID = 'incident-case-v2-view-injuries-and-damages'

/** Step 5, read only: each injury per person, then what was damaged. */
const InjuriesAndDamagesReadPanel = ({
  incidentReport
}: IncidentCaseV2ReadPanelProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { singular: nominative } = useServiceUserWords()
  const serviceUserNames = useServiceUserName()
  const teamMemberNames = useTeamMemberName()
  const { serviceUser } = incidentReport
  const savedCase = (incidentReport.case ?? {}) as InjuriesCase

  // The names the read embeds for People involved come first.
  const embedded = embeddedNames(savedCase)
  const withEmbedded = (names: PersonNames): PersonNames => ({
    ...names,
    nameOf: (id) => names.nameOf(id, id ? embedded.get(id) : undefined)
  })

  // Until step 5 is saved, what opening the case and the app recorded.
  const { damages, injuries } = step5Answers(savedCase)
  const isDamaged = damages?.hasBeenApplied
  const injuryDetails = detailsUnderNo(injuries)
  const damageDetails = detailsUnderNo(damages)

  const injuryBlocks = injuryEntries(injuries)

  const titleOf = (entry: Injury, number: number) =>
    injuryTitle(t, entry, number, {
      serviceUser: serviceUser.name,
      serviceUserWord: nominative
    })

  return (
    <>
      <ReadWide>
        <ReadItem
          label={t(
            incidentReportsConstants.CASE_V2_INJURIES_WAS_ANYONE_INJURED
          )}
          value={yesNo(t, injuries?.hasBeenApplied)}
          testId={`${TEST_ID}-is-injured`}
        />
      </ReadWide>
      {injuries?.hasBeenApplied === false && (
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
            value={injuryDetails}
            testId={`${TEST_ID}-injury-details`}
          />
        </ReadWide>
      )}
      {injuryBlocks.map(({ entry, number }, index) => (
        <ReadEntry
          // Entries have no id; the list is read only and never reordered.
          // biome-ignore lint/suspicious/noArrayIndexKey: see above
          key={index}
          title={titleOf(entry, number)}
          testId={`${TEST_ID}-injury-${index}`}
        >
          <InjuryItems
            bodyMapIds={injuryBodyMapIds(entry, savedCase)}
            entry={entry}
            residentId={serviceUser.id}
            serviceUserNames={withEmbedded(serviceUserNames)}
            teamMemberNames={withEmbedded(teamMemberNames)}
          />
        </ReadEntry>
      ))}

      <ReadWide>
        <ReadItem
          label={t(
            incidentReportsConstants.CASE_V2_INJURIES_WAS_ANYTHING_DAMAGED
          )}
          value={yesNo(t, isDamaged)}
          testId={`${TEST_ID}-is-damaged`}
        />
      </ReadWide>
      {isDamaged === false && (
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
            value={damageDetails}
            testId={`${TEST_ID}-damage-details`}
          />
        </ReadWide>
      )}
      {recordedDamages(damages).map((entry, index) => (
        <ReadEntry
          // Entries have no id; the list is read only and never reordered.
          // biome-ignore lint/suspicious/noArrayIndexKey: see above
          key={index}
          title={t(incidentReportsConstants.CASE_V2_VIEW_INJURIES_DAMAGE, {
            number: index + 1
          })}
          testId={`${TEST_ID}-damage-${index}`}
        >
          <ReadWide>
            <ReadItem
              label={t(incidentReportsConstants.CASE_V2_INJURIES_DAMAGE_TYPE)}
              value={t(DAMAGE_TYPE_KEYS[entry.type])}
            />
          </ReadWide>
          <ReadWide>
            <ReadItem
              label={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
              value={entry.details}
            />
          </ReadWide>
        </ReadEntry>
      ))}
    </>
  )
}

type InjuryItemsProps = {
  bodyMapIds: Array<string>
  entry: Injury
  residentId: string
  serviceUserNames: PersonNames
  teamMemberNames: PersonNames
}

/** One injury's answers, naming the people its ids point at. */
const InjuryItems = ({
  bodyMapIds,
  entry,
  residentId,
  serviceUserNames,
  teamMemberNames
}: InjuryItemsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const injured = injuredPerson(entry)
  const lookup =
    injured?.lookup === 'serviceUser'
      ? serviceUserNames
      : injured?.lookup === 'teamMember'
        ? teamMemberNames
        : null
  const person = lookup
    ? { isLoading: lookup.isLoading, name: lookup.nameOf(injured?.id) }
    : { isLoading: false, name: injured?.name }

  return (
    <>
      {bodyMapIds.length > 0 && (
        <LinkedBodyMaps ids={bodyMapIds} residentId={residentId} />
      )}
      {injured && (
        <ReadWide>
          <ReadItem
            isLoading={person.isLoading}
            label={t(incidentReportsConstants.CASE_V2_INJURIES_NAME)}
            value={person.name}
          />
        </ReadWide>
      )}
      <ReadWide>
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
          value={entry.details}
        />
      </ReadWide>
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_INJURIES_TREATMENT_GIVEN)}
        value={yesNo(t, entry.treatmentGiven)}
      />
      {entry.treatmentGiven && (
        <ReadItem
          isLoading={teamMemberNames.isLoading}
          label={t(incidentReportsConstants.CASE_V2_INJURIES_BY_WHOM)}
          value={teamMemberNames.nameOf(entry.treatmentGivenBy)}
        />
      )}
      {entry.treatmentGiven && (
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_INJURIES_TREATMENT)}
            value={entry.treatment}
          />
        </ReadWide>
      )}
    </>
  )
}

type LinkedBodyMapsProps = {
  ids: Array<string>
  residentId: string
}

/**
 * The body maps linked to the service user's injury, each linking to its
 * chart; a map that no longer resolves says so rather than vanish.
 */
const LinkedBodyMaps = ({ ids, residentId }: LinkedBodyMapsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { bodyMaps, isError, isLoading } = useBodyMaps({ residentId })

  return (
    <ReadWide>
      <ReadItem
        isLoading={isLoading}
        label={t(incidentReportsConstants.CASE_V2_INJURIES_RELATED_BODY_MAPS)}
        value={
          isError
            ? t(incidentReportsConstants.CASE_V2_VIEW_NOT_LOADED)
            : ids.map((id) => {
                const { bodyMap, label } = linkedBodyMap(t, id, bodyMaps)

                return bodyMap ? (
                  <Link
                    key={id}
                    to={`/residents/${bodyMap.resident}/charts/body_maps/${id}`}
                  >
                    {label}
                  </Link>
                ) : (
                  label
                )
              })
        }
        testId={`${TEST_ID}-body-maps`}
      />
    </ReadWide>
  )
}

export default InjuriesAndDamagesReadPanel
